import { useLayoutEffect, useRef, useState } from "react";
import { C, MONO } from "./ui-tokens";

export interface LevelWhiskerProps {
  /** Center of the estimate — the section/overall raw mean. Null = no reading. */
  mean: number | null;
  /** Half-width — the dispersion (population std-dev). Null collapses to a dot. */
  stddev: number | null;
  levels: { min: number; max: number };
  width?: number;
  /** Visual scale. `sm` (default) = the slim header meter; `lg` = the larger,
   *  more detailed read used in the level-explanation lead. */
  size?: "sm" | "lg";
  /** Individual scored-competency levels, plotted as small ticks on the rail so
   *  the actual spread (clustering / outliers) is visible, not just the band.
   *  Only rendered at `size="lg"`. */
  points?: number[];
  /** Optional round marker — e.g. the previous round's integer level — drawn as
   *  a filled ring on the rail so the user can see where the last scored round
   *  sat relative to the current estimate band. */
  marker?: { level: number; color?: string } | null;
  /** Fill the parent's width: the container is measured and its real pixel
   *  width drives the rail geometry, so the scale stretches while labels and
   *  markers keep their fixed size. `width` becomes the pre-measure fallback. */
  fluid?: boolean;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Per-size geometry. `sm` keeps the original header dimensions verbatim. */
const SIZING = {
  sm: { pad: 13, h: 46, cy: 15, label: 9, dot: 1.6, bar: 3, capH: 5, est: 6, estStroke: 2 },
  lg: { pad: 16, h: 66, cy: 24, label: 12, dot: 2, bar: 4, capH: 8, est: 8, estStroke: 2.5 },
} as const;

/**
 * Dot + whisker showing the estimate band on the model's level scale: the dot
 * sits at the mean, the bar spans mean ± one std-dev (clamped to range). With
 * no dispersion (single scored axis) only the dot renders; with no mean (all
 * abstained) nothing does. At `size="lg"` the rail is larger and each scored
 * competency is plotted as a tick so the spread is visible directly.
 */
export function LevelWhisker({
  mean,
  stddev,
  levels,
  width = 228,
  size = "sm",
  points,
  marker,
  fluid = false,
}: LevelWhiskerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!fluid) return;
    const el = hostRef.current;
    if (!el) return;
    const update = () => setMeasured(el.clientWidth || null);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fluid]);

  const z = SIZING[size];
  const w = fluid ? Math.max(180, measured ?? width) : width;
  const span = Math.max(1, levels.max - levels.min);
  const x = (n: number) => z.pad + ((n - levels.min) / span) * (w - 2 * z.pad);

  if (mean === null) {
    return <div style={{ ...UI_LABEL }}>Sem estimativa — competências insuficientes.</div>;
  }

  const est = clamp(mean, levels.min, levels.max);
  const lo = stddev === null ? est : clamp(mean - stddev, levels.min, levels.max);
  const hi = stddev === null ? est : clamp(mean + stddev, levels.min, levels.max);
  const xest = x(est);
  const xlo = x(lo);
  const xhi = x(hi);

  const ticks: number[] = [];
  for (let n = levels.min; n <= levels.max; n++) ticks.push(n);

  const svg = (
    <svg
      width={w}
      height={z.h}
      viewBox={`0 0 ${w} ${z.h}`}
      style={
        !fluid && size === "lg"
          ? {
              display: "block",
              overflow: "visible",
              width: "100%",
              maxWidth: w,
              height: "auto",
            }
          : { display: "block", overflow: "visible" }
      }
      role="img"
      aria-label={`Estimativa L${est.toFixed(1)} (faixa L${lo.toFixed(1)}–L${hi.toFixed(1)})`}
    >
      <line
        x1={z.pad}
        y1={z.cy}
        x2={w - z.pad}
        y2={z.cy}
        stroke="rgba(31,27,23,0.12)"
        strokeWidth={1}
      />
      {ticks.map((n) => {
        const inRange = n >= Math.floor(lo) && n <= Math.ceil(hi);
        return (
          <g key={n}>
            <circle cx={x(n)} cy={z.cy} r={z.dot} fill={C.ink20} />
            <text
              x={x(n)}
              y={z.h - 2}
              textAnchor="middle"
              style={{
                fontFamily: MONO.fontFamily,
                fontVariantNumeric: MONO.fontVariantNumeric,
                fontSize: z.label,
                fill: inRange ? "rgba(31,27,23,0.75)" : C.ink32,
              }}
            >
              L{n}
            </text>
          </g>
        );
      })}
      {size === "lg" &&
        points?.map((n, i) => {
          const cx = x(clamp(n, levels.min, levels.max));
          return (
            <line
              // biome-ignore lint/suspicious/noArrayIndexKey: points are plain levels, order is positional
              key={`pt-${i}`}
              x1={cx}
              y1={z.cy - 7}
              x2={cx}
              y2={z.cy + 7}
              stroke={C.ink32}
              strokeWidth={2}
              strokeLinecap="round"
            />
          );
        })}
      {stddev !== null && (
        <line
          x1={xlo}
          y1={z.cy}
          x2={xhi}
          y2={z.cy}
          stroke="rgba(31,27,23,0.18)"
          strokeWidth={z.bar + 3}
          strokeLinecap="round"
        />
      )}
      {/* The estimate: a precise coral tick with the mean labeled at the marker
       *  (not in a footnote) — coral marks the estimate, grey marks the range. */}
      <line
        x1={xest}
        y1={z.cy - z.capH - 1}
        x2={xest}
        y2={z.cy + z.capH + 1}
        stroke={C.red}
        strokeWidth={2}
        strokeLinecap="round"
      />
      {size === "lg" && (
        <text
          x={xest}
          y={z.cy - z.capH - 5}
          textAnchor="middle"
          style={{
            fontFamily: MONO.fontFamily,
            fontVariantNumeric: MONO.fontVariantNumeric,
            fontSize: z.label,
            fontWeight: 600,
            fill: C.red,
          }}
        >
          {est.toFixed(1).replace(".", ",")}
        </text>
      )}
      {marker && (
        <g>
          <line
            x1={x(clamp(marker.level, levels.min, levels.max))}
            y1={z.cy}
            x2={x(clamp(marker.level, levels.min, levels.max))}
            y2={z.cy + 14}
            stroke={C.ink32}
            strokeWidth={1}
            strokeDasharray="2 2"
          />
          <circle
            cx={x(clamp(marker.level, levels.min, levels.max))}
            cy={z.cy + 14}
            r={z.est + 1.5}
            fill={marker.color ?? C.ink}
            stroke={C.white}
            strokeWidth={2}
          />
        </g>
      )}
    </svg>
  );

  if (!fluid) return svg;
  return (
    <div ref={hostRef} style={{ width: "100%", minWidth: 0 }}>
      {svg}
    </div>
  );
}

const UI_LABEL: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: 11.5,
  color: C.ink50,
};
